import { createSelector, createStructuredSelector } from 'reselect';
import { flow, identity, isEqual, map, constant } from 'lodash/fp';

import {
  HP_TO_KW,
  HP,
  options as powerTypeOptions,
} from 'helpers/fieldOptions/car/powerType';

import { options as airbagOptions } from 'helpers/fieldOptions/car/airbag';
import { options as additionalOptions } from 'helpers/fieldOptions/car/additional';
import { options as comfortOptions } from 'helpers/fieldOptions/car/comfort';
import { options as consumptionOptions } from 'helpers/fieldOptions/car/consumption';
import { options as envStandardOptions } from 'helpers/fieldOptions/car/environmentalStandard';
import { options as emissionStickerOptions } from 'helpers/fieldOptions/car/emissionSticker';
import { options as seatComfortOptions } from 'helpers/fieldOptions/car/seatComfort';
import { options as multimediaOptions } from 'helpers/fieldOptions/car/multimedia';
import { options as lightOptions } from 'helpers/fieldOptions/car/light';
import {
  options as bodyTypeOptions,
  commercialOptions as commercialBodyTypeOptions,
} from 'helpers/fieldOptions/car/bodyType';
import { options as colorOptions } from 'helpers/fieldOptions/car/color';
import { options as doorOptions } from 'helpers/fieldOptions/car/door';
import { options as extraOptions } from 'helpers/fieldOptions/car/extra';
import { options as fuelTypeOptions } from 'helpers/fieldOptions/car/fuelType';
import { options as interiorOptions } from 'helpers/fieldOptions/car/interior';
import { selectManufacturerOptions } from 'store/ducks/manufacturers';
import { options as metallicOptions } from 'helpers/fieldOptions/car/metallic';
import { options as sellerTypeOptions } from 'helpers/fieldOptions/ad/sellerType';
import { options as parkingSensorOptions } from 'helpers/fieldOptions/car/parkingSensor';
import { options as powerOptions } from 'helpers/fieldOptions/car/power';
import { options as prevOwnerOptions } from 'helpers/fieldOptions/car/prevOwner';
import { options as seatOptions } from 'helpers/fieldOptions/car/seat';
import { options as securityOptions } from 'helpers/fieldOptions/car/security';
import { options as serviceHistoryOptions } from 'helpers/fieldOptions/car/serviceHistory';
import { options as transmissionOptions } from 'helpers/fieldOptions/car/transmission';
import { options as upholsteryOptions } from 'helpers/fieldOptions/car/upholstery';
import {
  options as priceTypeOptions,
  BUY_PRICE,
} from 'helpers/fieldOptions/ad/priceType';
import { getMinMaxOptions } from 'helpers/fieldOptions';
import createFormValueSelector from 'helpers/createFormValueSelector';
import { formatPower } from 'helpers/i18n';

import { isMonthlyPriceAvailableFeature } from 'modules/features';

import {
  POWER_RANGE_NAME,
  POWER_TYPE_NAME,
  PRICE_TYPE_NAME,
} from '../../constants';

const getFormValue = (path) => (state, props) =>
  createFormValueSelector(props.formName)(path)(state);

const createMinMaxSelector = ({ type, field, options, formatter = identity }) =>
  createSelector(
    getFormValue([field]),
    flow(
      (value) => getMinMaxOptions({ options, type, value }),
      map((item) => ({ ...item, label: formatter(item.label) })),
    ),
  );

const getPowerOptions = (type) =>
  createSelector(
    createMinMaxSelector({
      type,
      field: POWER_RANGE_NAME,
      options: powerOptions,
    }),
    getFormValue([POWER_TYPE_NAME]),
    (options, powerType) => {
      const formatter =
        powerType === HP
          ? (hp) => formatPower({ hp })
          : (hp) => formatPower({ kw: Math.ceil(hp * HP_TO_KW) });

      return map(
        (item) => ({ ...item, label: formatter(item.value) }),
        options,
      );
    },
  );

const getShowPriceOption = createSelector(
  getFormValue([PRICE_TYPE_NAME]),
  isEqual(BUY_PRICE),
);

const getOptions = createStructuredSelector({
  airbag: () => airbagOptions,
  additional: () => additionalOptions,
  comfort: () => comfortOptions,
  bodyType: () => bodyTypeOptions,
  commercialBodyType: () => commercialBodyTypeOptions,
  color: () => colorOptions,
  consumptionRange: () => consumptionOptions,
  doorRange: () => doorOptions,
  envStandard: () => envStandardOptions,
  emissionSticker: () => emissionStickerOptions,
  extra: () => extraOptions,
  fuelType: () => fuelTypeOptions,
  interior: () => interiorOptions,
  light: () => lightOptions,
  make: selectManufacturerOptions,
  metallic: () => metallicOptions,
  multimedia: () => multimediaOptions,
  parkingSensor: () => parkingSensorOptions,
  powerMax: getPowerOptions('max'),
  powerMin: getPowerOptions('min'),
  powerType: () => powerTypeOptions,
  prevOwner: () => prevOwnerOptions,
  priceType: () => priceTypeOptions,
  seatComfort: () => seatComfortOptions,
  seatRange: () => seatOptions,
  security: () => securityOptions,
  sellerType: () => sellerTypeOptions,
  serviceHistory: () => serviceHistoryOptions,
  transmission: () => transmissionOptions,
  upholstery: () => upholsteryOptions,
});

export default createStructuredSelector({
  options: getOptions,
  showMonthlyPrice: constant(isMonthlyPriceAvailableFeature),
  showPriceOption: getShowPriceOption,
});
